/**
 * secret-santa/index.js — «Тайный Санта»: a list of names, the pairs that must
 * not draw each other, one draw, and two ways to learn your match without
 * anyone else learning it:
 *   · PHONE — the phone goes round; each person taps their name, confirms,
 *     reads, hides. The draw lives in this tab only (nothing leaves the page),
 *     and leaving the tab mid-round asks first (__unsavedWork).
 *   · CARDS — a PDF of fold-over cards: the giver's name on a tab outside,
 *     the match inside the fold.
 * No daily cap: a party game (CLAUDE.md — games are not capped), like the
 * spin wheel next to it.
 */
import { holidaysFor } from '../printable-masks/catalog.js';
import { parseParticipants, parseExclusions, draw } from './draw.js';
import { buildCardsPdf } from './cards.js';
import { ensurePdfLib } from '../printable-masks/pdf.js';

const SLUG = 'secret-santa';
const CFG = window.__SANTA__ || {};
const T = CFG.t || {};
const t = (k, fb) => (T[k] != null ? T[k] : (fb != null ? fb : k));
const $ = id => document.getElementById(id);
const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const state = { mode: 'phone', people: [], pairs: [], result: null, seen: new Set(), open: -1, step: 'idle' };

function readInputs() {
    state.people = parseParticipants($('ssNames').value);
    const ex = parseExclusions($('ssExclude').value, state.people);
    state.pairs = ex.pairs;
    $('ssCount').textContent = t('count_line', 'Participants: :n').replace(':n', state.people.length);
    const warn = $('ssExWarn');
    warn.hidden = !ex.unknown.length;
    if (ex.unknown.length) warn.textContent = t('ex_unknown', 'Not found: :list').replace(':list', ex.unknown.join('; '));
    const btn = $('ssDraw');
    const ok = state.people.length >= 3;
    btn.disabled = !ok;
    $('ssDrawWhy').hidden = ok;
}

function details() {
    return { budget: $('ssBudget').value.trim(), date: $('ssDate').value.trim(), note: $('ssNote').value.trim() };
}

/* ── the draw ───────────────────────────────────────────────────────── */

function doDraw() {
    readInputs();
    if (state.people.length < 3) return;
    const r = draw(state.people.length, state.pairs);
    if (!r.ok) {
        state.result = null;
        $('ssResult').innerHTML = '<p class="ss-err" role="alert">' + esc(t('err_' + r.reason, t('err_impossible'))) + '</p>';
        return;
    }
    state.result = r.to; state.seen = new Set(); state.open = -1; state.step = 'grid';
    if (window.__unsavedWork && state.mode === 'phone') window.__unsavedWork.arm('santa:round');
    if (window.__aeToolComplete) window.__aeToolComplete({ ok: true, n: state.people.length, ex: state.pairs.length, mode: state.mode });
    renderResult();
}

function renderResult() {
    const box = $('ssResult');
    if (!state.result) { box.innerHTML = ''; return; }
    if (state.mode === 'cards') {
        box.innerHTML = '<p class="ss-ok">' + esc(t('cards_ready', '').replace(':n', state.people.length)) + '</p>'
            + '<button type="button" class="tc-btn" id="ssDlPdf">' + esc(t('save_pdf')) + '</button>'
            + '<p class="tc-hint">' + esc(t('cards_hint')) + '</p>';
        return;
    }
    if (state.step === 'confirm') {
        const p = state.people[state.open];
        box.innerHTML = '<div class="ss-card"><p class="ss-big">' + esc(t('confirm_q', 'Is it you, :name?').replace(':name', p.name)) + '</p>'
            + '<p class="tc-hint">' + esc(t('confirm_hint')) + '</p>'
            + '<div class="ss-row"><button type="button" class="tc-btn" data-ssact="reveal">' + esc(t('btn_yes')) + '</button>'
            + '<button type="button" class="tc-btn ghost" data-ssact="back">' + esc(t('btn_no')) + '</button></div></div>';
        return;
    }
    if (state.step === 'reveal') {
        const g = state.people[state.open], r = state.people[state.result[state.open]], d = details();
        box.innerHTML = '<div class="ss-card ss-reveal"><p>' + esc(t('reveal_to', ':name, you give a gift to').replace(':name', g.name)) + '</p>'
            + '<p class="ss-match">' + esc(r.name) + '</p>'
            + (r.wish ? '<p>' + esc(t('wish_line', 'Wish: :w').replace(':w', r.wish)) + '</p>' : '')
            + (d.budget ? '<p>' + esc(t('budget_line', 'Budget: :b').replace(':b', d.budget)) + '</p>' : '')
            + (d.date ? '<p>' + esc(t('date_line', 'When: :d').replace(':d', d.date)) + '</p>' : '')
            + (d.note ? '<p>' + esc(d.note) + '</p>' : '')
            + '<button type="button" class="tc-btn" data-ssact="hide">' + esc(t('btn_hide')) + '</button></div>';
        return;
    }
    const all = state.seen.size === state.people.length;
    box.innerHTML = '<p class="tc-hint">' + esc(all ? t('round_done') : t('round_hint')) + '</p><div class="ss-names">'
        + state.people.map((p, i) => '<button type="button" class="ss-name' + (state.seen.has(i) ? ' seen' : '') + '" data-ssname="' + i + '">'
            + (state.seen.has(i) ? '✓ ' : '') + esc(p.name) + '</button>').join('') + '</div>';
    if (all && window.__unsavedWork) window.__unsavedWork.disarm();
}

/* ── output ─────────────────────────────────────────────────────────── */

async function saveCards() {
    if (!state.result) return;
    const btn = $('ssDlPdf'); const was = btn.textContent;
    btn.disabled = true; btn.textContent = t('building', '…');
    try {
        const d = details();
        const cards = state.people.map((p, i) => ({ giver: p.name, receiver: state.people[state.result[i]].name, wish: state.people[state.result[i]].wish }));
        const blob = await buildCardsPdf({
            paper: /-(US|CA)$/i.test(navigator.language || '') ? 'letter' : 'a4', cards, details: d,
            strings: { for: t('card_for'), title: t('card_title'), wish: t('wish_line'), budget: t('budget_line'), date: t('date_line'),
                greeting: t('card_greeting'), footer: t('cards_footer'), ruler: t('pdf_ruler') },
            title: t('file_prefix', 'secret-santa'),
        });
        btn.disabled = false; btn.textContent = was;
        const name = window.__outputName ? window.__outputName.join([window.__outputName.sanitize(t('file_prefix', 'secret-santa'))], 'pdf') : 'secret-santa.pdf';
        await window.__downloadFile(blob, name);
        if (window.__aeToolComplete) window.__aeToolComplete({ ok: true, f: 'pdf', n: cards.length });
    } catch (err) {
        if (window.__handleError) window.__handleError(err, 'santa_cards', { say: t('err_pdf') });
    } finally { btn.disabled = false; btn.textContent = was; }
}

/* ── wiring ─────────────────────────────────────────────────────────── */

function setMode(m) {
    state.mode = m;
    document.querySelectorAll('[data-ssmode]').forEach(b => { const on = b.dataset.ssmode === m; b.classList.toggle('on', on); b.setAttribute('aria-pressed', String(on)); });
    $('ssModeHint').textContent = t('mode_hint_' + m);
    if (state.result) { state.step = 'grid'; renderResult(); }
}

function init() {
    $('ssNames').value = t('example_names', '');
    const hs = holidaysFor(window.__LOCALE || 'en');
    const ny = hs.find(h => h.id === 'newyear');
    if (ny) { $('ssHol').hidden = false; $('ssHol').textContent = t('hol_soon', ':name').replace(':name', t('hol_newyear')); }
    readInputs();
    setMode('phone');
    $('ssNames').addEventListener('input', () => { readInputs(); state.result = null; renderResult(); if (window.__unsavedWork) window.__unsavedWork.disarm(); });
    $('ssExclude').addEventListener('input', () => { readInputs(); state.result = null; renderResult(); });
    document.addEventListener('click', e => {
        const el = e.target.closest('[data-ssmode],[data-ssname],[data-ssact],#ssDraw,#ssDlPdf');
        if (!el || !el.closest('.ss')) return;
        const d = el.dataset;
        if (d.ssmode) setMode(d.ssmode);
        else if (el.id === 'ssDraw') {
            if (state.result && state.seen.size && !window.confirm(t('redraw_q'))) return;
            doDraw();
        } else if (el.id === 'ssDlPdf') saveCards();
        else if (d.ssname != null) {
            const i = +d.ssname;
            if (state.seen.has(i) && !window.confirm(t('again_q', '').replace(':name', state.people[i].name))) return;
            state.open = i; state.step = 'confirm'; renderResult();
        } else if (d.ssact === 'reveal') { state.step = 'reveal'; renderResult(); }
        else if (d.ssact === 'back') { state.step = 'grid'; state.open = -1; renderResult(); }
        else if (d.ssact === 'hide') { state.seen.add(state.open); state.step = 'grid'; state.open = -1; renderResult(); }
    });
    const warm = () => { document.querySelector('.ss').removeEventListener('pointerdown', warm, true); ensurePdfLib().catch(() => { }); };
    document.querySelector('.ss').addEventListener('pointerdown', warm, true);
    try { if (window.__stage) window.__stage('ready'); } catch (e) { }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
